import { GeoJSON, LatLng, LatLngTuple, LatLngExpression } from 'leaflet';
import { decode } from '@here/flexpolyline';
import { uniqBy } from 'lodash-es';
import {
  along as turfAlong,
  bezierSpline,
  length as turfLength,
  lineString as turfLineString,
  point as turfPoint,
} from '@turf/turf';
import { parseZonedDateTime } from '@internationalized/date';
import {
  AircraftLegResource,
  AviaLegTypes,
  BaseLegResourceType,
  GroundLegTypes,
  LegLocationTypeEnum,
  RouteLegTypeEnum,
  RouteLocationType,
  SegmentTypeEnum,
} from '@/api/apis/route';
import { CalculateGroundTimeType } from '@/api/apis/here-maps';
import { CaseResourceType } from '@/api/apis/case';
import { queryClient } from '@/queries';
import { hereMapsQueries } from '@/queries/here-maps/here-maps.query';
import { getTzDateTimeString } from '@/utils/helpers';

export type RouteMarkerType = {
  id: string;
  location: LatLngExpression;
};

export type RoutePolylineType = {
  id: string;
  type: SegmentTypeEnum;
  legType: RouteLegTypeEnum;
  positions: LatLngExpression[] | LatLngExpression[][];
  direction: 'left' | 'right';
  index: string;
  middlePosition?: LatLngExpression;
  zoomLevel?: number;
};

export type FboMarkerType = {
  type: SegmentTypeEnum;
  code: string;
  title: string;
} & RouteMarkerType;

export const TRANSPORT_PROVIDER_ABBR: Record<RouteLegTypeEnum, string> = {
  [RouteLegTypeEnum.TmdxJetAviaLeg]: '',
  [RouteLegTypeEnum.CurriServiceGroundLeg]: 'COM',
  [RouteLegTypeEnum.TxCenterProviderGroundLeg]: 'TX',
  [RouteLegTypeEnum.TxCenterProviderAviaLeg]: 'TX',
  [RouteLegTypeEnum.SupplementalLiftAviaLeg]: 'SLP',
  [RouteLegTypeEnum.TmdxAffiliateGroundLeg]: 'A',
  [RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg]: 'A',
  [RouteLegTypeEnum.TmdxDriveTedsGroundLeg]: 'TEDS',
  [RouteLegTypeEnum.CommercialFlightAviaLeg]: 'COM',
  [RouteLegTypeEnum.AccommodationLeg]: '',
  [RouteLegTypeEnum.SelfServicedGroundLeg]: 'S',
};

export const getAirMiddlePoint = (coordsStart: LatLng, coordsEnd: LatLng): LatLng => {
  const offsetX = coordsEnd.lng - coordsStart.lng;
  const offsetY = coordsEnd.lat - coordsStart.lat;

  const r = Math.sqrt(offsetX ** 2 + offsetY ** 2);
  const theta = Math.atan2(offsetY, offsetX);

  const thetaOffset = 3.14 / 50;

  const r2 = r / 2 / Math.cos(thetaOffset);
  const theta2 = theta + thetaOffset;

  const midpointX = r2 * Math.cos(theta2) + coordsStart.lng;
  const midpointY = r2 * Math.sin(theta2) + coordsStart.lat;

  return new LatLng(midpointY, midpointX);
};

export const getCurvedPolyline = (startLocation: LatLng, endLocation: LatLng): LatLng[] => {
  const start = turfPoint([startLocation.lng, startLocation.lat]);
  const end = turfPoint([endLocation.lng, endLocation.lat]);

  const midpoint = getAirMiddlePoint(endLocation, startLocation);

  return GeoJSON.coordsToLatLngs(
    bezierSpline({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: [start.geometry.coordinates, [midpoint.lng, midpoint.lat], end.geometry.coordinates],
      },
    }).geometry.coordinates,
  ) as LatLng[];
};

export const getGroundMiddlePoint = (coordinates: LatLngTuple[]): LatLng => {
  const coordsToGeo = coordinates.map((i) => turfPoint([i[1], i[0]]).geometry.coordinates);
  const line = turfLineString(coordsToGeo);
  const totalDistance = turfLength(line, { units: 'kilometers' });
  const center = turfAlong(line, totalDistance / 2, { units: 'kilometers' });

  return new LatLng(center.geometry.coordinates[1], center.geometry.coordinates[0]);
};

export const generatePathRouting = async (
  legs: (AviaLegTypes | GroundLegTypes)[],
  locations: RouteLocationType[],
  aircrafts: BaseLegResourceType<AircraftLegResource>[] = [],
): Promise<RoutePolylineType[]> => {
  const isMultipleAircrafts = aircrafts.length > 1;
  let polylineArray: RoutePolylineType[] = [];

  if (!legs.length) {
    return polylineArray;
  }

  for await (const leg of legs) {
    if (leg.departureLocation.locationId && leg.arrivalLocation.locationId) {
      const startLocation = locations.find((l) => l.id === leg.departureLocation.locationId);
      const endLocation = locations.find((l) => l.id === leg.arrivalLocation.locationId);

      if (!startLocation || !endLocation) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const start = new LatLng(startLocation.location.latitude, startLocation.location.longitude);
      const end = new LatLng(endLocation.location.latitude, endLocation.location.longitude);
      const direction = start.lng > end.lng ? 'left' : 'right';
      const currentAircraft = leg.resources.find((l) => l.resourceType === CaseResourceType.Aircraft);
      const currentAircraftIndex = isMultipleAircrafts
        ? aircrafts.findIndex((a) => a.id === currentAircraft?.resourceId)
        : -1;
      const index = currentAircraftIndex + 1 > 0 ? `${currentAircraftIndex + 1}` : '';
      const typeIndex = TRANSPORT_PROVIDER_ABBR[leg.legType];

      switch (leg.legType) {
        case RouteLegTypeEnum.TmdxJetAviaLeg:
        case RouteLegTypeEnum.SupplementalLiftAviaLeg:
        case RouteLegTypeEnum.TxCenterProviderAviaLeg:
        case RouteLegTypeEnum.CommercialFlightAviaLeg:
          polylineArray = [
            ...polylineArray,
            {
              id: `route-${leg.id}`,
              type: leg.segmentType,
              legType: leg.legType,
              positions: getCurvedPolyline(start, end),
              middlePosition: getAirMiddlePoint(end, start),
              direction,
              index: typeIndex || index,
            },
          ];

          break;
        case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
        case RouteLegTypeEnum.TxCenterProviderGroundLeg:
        case RouteLegTypeEnum.CurriServiceGroundLeg:
        case RouteLegTypeEnum.SelfServicedGroundLeg:
          const departureTimeUtc = parseZonedDateTime(getTzDateTimeString(leg.departureTime)).toAbsoluteString();

          const { polylines } = await queryClient.ensureQueryData(
            hereMapsQueries.getGroundRoute({
              startLocation: start,
              endLocation: end,
              type: CalculateGroundTimeType.DepartureTime,
              timestamp: departureTimeUtc,
            }),
          );

          const decoded = polylines
            .map((p) => {
              const data = decode(p);

              return data.polyline as LatLngTuple[];
            })
            .flat();

          if (!decoded.length) {
            break;
          }

          polylineArray = [
            ...polylineArray,
            {
              id: `route-${leg.id}`,
              type: leg.segmentType,
              legType: leg.legType,
              positions: decoded,
              middlePosition: getGroundMiddlePoint(decoded),
              index: typeIndex,
              direction,
            },
          ];

          break;

        default:
          break;
      }
    }
  }

  return polylineArray;
};

export const generateMarkers = <T extends RouteMarkerType>(
  legs: (AviaLegTypes | GroundLegTypes)[],
  locationType: LegLocationTypeEnum,
  mapper: (locationId: string) => T,
): T[] => {
  const locations = legs
    .filter((l) => l.departureLocation.locationType === locationType || l.arrivalLocation.locationType === locationType)
    .map((l) => {
      const isStartLocation = l.departureLocation.locationType === locationType;
      const isEndLocation = l.arrivalLocation.locationType === locationType;

      return [
        ...(isStartLocation ? [mapper(l.departureLocation.locationId)] : []),
        ...(isEndLocation ? [mapper(l.arrivalLocation.locationId)] : []),
      ];
    })
    .flat();

  return uniqBy<T>(locations, 'id');
};
